import { useState, useEffect, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Plus,
  Pencil,
  UserX,
  UserCheck,
  CheckCircle2,
  KeyRound,
  ChevronDown,
  Send,
  FileUp,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { UserRole, UserStatus } from '@barrikadave/shared';
import type { IUser, IUserCreate, IUserUpdate } from '@barrikadave/shared';
import { usersApi, type UsersParams } from '../services/api';
import Modal from '../components/Modal';
import CsvImportWizard from '../components/csv-import/CsvImportWizard';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge, { getRoleBadgeVariant, getStatusBadgeVariant } from '../components/Badge';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';

const ROLES = Object.values(UserRole);
const STATUSES = Object.values(UserStatus);

interface UserFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  phone: string;
  position: string;
}

const emptyFormData: UserFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: UserRole.USER,
  phone: '',
  position: '',
};

export default function UsersPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [formData, setFormData] = useState<UserFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [deactivateUser, setDeactivateUser] = useState<IUser | null>(null);
  const [activateUser, setActivateUser] = useState<IUser | null>(null);
  const [approveUser, setApproveUser] = useState<IUser | null>(null);

  const [sendManualUser, setSendManualUser] = useState<IUser | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const [resetPasswordUser, setResetPasswordUser] = useState<IUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [sendEmailOnCreate, setSendEmailOnCreate] = useState(false);
  const [sendEmailOnReset, setSendEmailOnReset] = useState(false);

  const params: UsersParams = {
    page,
    limit,
    ...(search && { search }),
    ...(roleFilter && { role: roleFilter }),
    ...(statusFilter && { status: statusFilter }),
  };

  const usersQuery = useQuery({
    queryKey: ['users', params],
    queryFn: () => usersApi.getAll(params),
  });

  const createMutation = useMutation({
    mutationFn: (data: IUserCreate) => usersApi.create(data),
    onSuccess: () => {
      toast.success('Perdoruesi u krijua me sukses');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeCreateModal();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi krijimi i perdoruesit');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUserUpdate }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      toast.success('Perdoruesi u perditesua me sukses');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeEditModal();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi perditesimi');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => usersApi.deactivate(id),
    onSuccess: () => {
      toast.success('Perdoruesi u c\'aktivizua');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeactivateUser(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi c\'aktivizimi');
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => usersApi.activate(id),
    onSuccess: () => {
      toast.success('Perdoruesi u aktivizua');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setActivateUser(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi aktivizimi');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => usersApi.approve(id),
    onSuccess: () => {
      toast.success('Perdoruesi u aprovua');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-pending-count'] });
      setApproveUser(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi aprovimi');
    },
  });

  const [deleteUserTarget, setDeleteUserTarget] = useState<IUser | null>(null);

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      toast.success('Perdoruesi u fshi me sukses');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteUserTarget(null);
      closeEditModal();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi fshirja e perdoruesit');
    },
  });

  const sendManualMutation = useMutation({
    mutationFn: (id: string) => usersApi.sendManual(id),
    onSuccess: () => {
      toast.success('Manuali u dergua me sukses');
      setSendManualUser(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi dergimi i manualit');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password, sendEmail }: { id: string; password: string; sendEmail?: boolean }) =>
      usersApi.resetPassword(id, password, sendEmail),
    onSuccess: () => {
      toast.success(sendEmailOnReset ? 'Fjalekalimi u rivendos dhe u dergua me email' : 'Fjalekalimi u rivendos me sukses');
      setResetPasswordUser(null);
      setNewPassword('');
      setSendEmailOnReset(false);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi rivendosja e fjalkalimit');
    },
  });

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  function closeCreateModal() {
    setIsCreateModalOpen(false);
    setFormData(emptyFormData);
    setFormErrors({});
  }

  function closeEditModal() {
    setEditingUser(null);
    setFormData(emptyFormData);
    setFormErrors({});
  }

  function openEditModal(user: IUser) {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      phone: user.phone || '',
      position: user.position || '',
    });
    setFormErrors({});
  }

  function validateForm(isEdit: boolean): boolean {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim() || formData.firstName.length < 2) {
      errors.firstName = 'Emri duhet te kete te pakten 2 karaktere';
    }
    if (!formData.lastName.trim() || formData.lastName.length < 2) {
      errors.lastName = 'Mbiemri duhet te kete te pakten 2 karaktere';
    }
    if (!isEdit) {
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Email i pavlefshme';
      }
      if (!formData.password || formData.password.length < 8) {
        errors.password = 'Fjalekalimi duhet te kete te pakten 8 karaktere';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleCreateSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validateForm(false)) return;

    const createData: IUserCreate & { sendEmail?: boolean } = {
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
      ...(formData.phone && { phone: formData.phone }),
      ...(formData.position && { position: formData.position }),
      sendEmail: sendEmailOnCreate,
    };

    createMutation.mutate(createData);
  }

  function handleUpdateSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editingUser || !validateForm(true)) return;

    const updateData: IUserUpdate = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
      ...(formData.phone !== (editingUser.phone || '') && { phone: formData.phone }),
      ...(formData.position !== (editingUser.position || '') && { position: formData.position }),
    };

    updateMutation.mutate({ id: editingUser.id, data: updateData });
  }

  function handleResetPassword() {
    setResetPasswordError('');
    if (!resetPasswordUser) return;
    if (!newPassword || newPassword.length < 8) {
      setResetPasswordError('Fjalekalimi duhet te kete te pakten 8 karaktere');
      return;
    }
    resetPasswordMutation.mutate({ id: resetPasswordUser.id, password: newPassword, sendEmail: sendEmailOnReset });
  }

  const users = usersQuery.data?.data ?? [];
  const pagination = usersQuery.data?.pagination;

  const userFormFields = (isEdit: boolean) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Emri *</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
            className="input-field"
            placeholder="Emri"
          />
          {formErrors.firstName && (
            <p className="text-xs text-red-500 mt-1">{formErrors.firstName}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mbiemri *</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
            className="input-field"
            placeholder="Mbiemri"
          />
          {formErrors.lastName && (
            <p className="text-xs text-red-500 mt-1">{formErrors.lastName}</p>
          )}
        </div>
      </div>

      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
            className="input-field"
            placeholder="email@shembull.com"
          />
          {formErrors.email && (
            <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
          )}
        </div>
      )}

      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fjalekalimi *</label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
            className="input-field"
            placeholder="Te pakten 8 karaktere"
          />
          {formErrors.password && (
            <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>
          )}
          <label className="flex items-center gap-2 cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={sendEmailOnCreate}
              onChange={(e) => setSendEmailOnCreate(e.target.checked)}
              className="rounded border-gray-300 text-barrikadave-500 focus:ring-barrikadave-500"
            />
            <span className="text-sm text-gray-600">Dergo fjalekalimin me email</span>
          </label>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Roli</label>
          <select
            value={formData.role}
            onChange={(e) =>
              setFormData((p) => ({ ...p, role: e.target.value as UserRole }))
            }
            className="select-field"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
            className="input-field"
            placeholder="+355 6X XXX XXXX"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pozicioni</label>
        <input
          type="text"
          value={formData.position}
          onChange={(e) => setFormData((p) => ({ ...p, position: e.target.value }))}
          className="input-field"
          placeholder="p.sh. Deputet, Minister"
        />
      </div>

    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Perdoruesit</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsImportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileUp className="h-4 w-4" />
            <span>Importo CSV</span>
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            <span>Shto Perdorues</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kerko sipas emrit ose emailit..."
              className="input-field pl-9"
            />
          </div>
          <div className="relative">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="select-field pr-8 min-w-[140px]"
            >
              <option value="">Te gjithe rolet</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select-field pr-8 min-w-[140px]"
            >
              <option value="">Te gjithe statuset</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden !p-0">
        {usersQuery.isLoading ? (
          <LoadingSpinner className="py-16" />
        ) : usersQuery.isError ? (
          <div className="text-center py-16">
            <p className="text-red-500 text-sm">Ndodhi nje gabim gjate ngarkimit te perdoruesve.</p>
            <button
              onClick={() => usersQuery.refetch()}
              className="btn-secondary mt-3 text-sm"
            >
              Provo perseri
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-sm">Nuk u gjeten perdorues.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Emri
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Roli
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Statusi
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Hyrja e fundit
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Veprime
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0">
                          <span className="text-barrikadave-600 font-semibold text-xs">
                            {user.firstName[0]}
                            {user.lastName[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </p>
                          {user.position && (
                            <p className="text-xs text-gray-400">{user.position}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={getRoleBadgeVariant(user.role)}>{user.role}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={getStatusBadgeVariant(user.status)}>
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.lastLoginAt
                        ? format(new Date(user.lastLoginAt), 'dd/MM/yyyy HH:mm')
                        : <span className="text-gray-300">Asnjehere</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(user)}
                          title="Ndrysho"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-barrikadave-600 hover:bg-barrikadave-50 transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {user.status === UserStatus.PENDING && (
                          <button
                            onClick={() => setApproveUser(user)}
                            title="Aprovo"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                        )}
                        {user.status === UserStatus.ACTIVE ? (
                          <button
                            onClick={() => setDeactivateUser(user)}
                            title="C'aktivizo"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <UserX className="h-4 w-4" />
                          </button>
                        ) : user.status !== UserStatus.PENDING ? (
                          <button
                            onClick={() => setActivateUser(user)}
                            title="Aktivizo"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        ) : null}
                        <button
                          onClick={() => {
                            setResetPasswordUser(user);
                            setNewPassword('');
                            setResetPasswordError('');
                          }}
                          title="Rivendos fjalekalimin"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                        >
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setSendManualUser(user)}
                          title="Dergo manualin"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-barrikadave-600 hover:bg-barrikadave-50 transition-colors"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-500">
                  Shfaqen {users.length} nga {pagination.total} perdorues
                </p>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value={50}>50 / faqe</option>
                  <option value={100}>100 / faqe</option>
                  <option value={150}>150 / faqe</option>
                </select>
              </div>
              {pagination.totalPages > 1 && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Shto Perdorues te Ri"
        size="lg"
      >
        <form onSubmit={handleCreateSubmit}>
          {userFormFields(false)}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button type="button" onClick={closeCreateModal} className="btn-ghost">
              Anulo
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Duke krijuar...' : 'Krijo Perdorues'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={!!editingUser}
        onClose={closeEditModal}
        title={`Ndrysho: ${editingUser?.firstName} ${editingUser?.lastName}`}
        size="lg"
      >
        <form onSubmit={handleUpdateSubmit}>
          {userFormFields(true)}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => editingUser && setDeleteUserTarget(editingUser)}
              className="text-sm text-red-500 hover:text-red-700 transition-colors"
            >
              Fshi perdoruesin
            </button>
            <div className="flex gap-3">
              <button type="button" onClick={closeEditModal} className="btn-ghost">
                Anulo
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn-primary"
              >
                {updateMutation.isPending ? 'Duke ruajtur...' : 'Ruaj Ndryshimet'}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Deactivate Confirm */}
      <ConfirmDialog
        isOpen={!!deactivateUser}
        onClose={() => setDeactivateUser(null)}
        onConfirm={() => deactivateUser && deactivateMutation.mutate(deactivateUser.id)}
        title="C'aktivizo Perdoruesin"
        message={`Jeni te sigurt qe deshironi te c'aktivizoni ${deactivateUser?.firstName} ${deactivateUser?.lastName}? Perdoruesi nuk do te mund te hyje me ne sistem.`}
        confirmText="C'aktivizo"
        variant="danger"
        isLoading={deactivateMutation.isPending}
      />

      {/* Approve Confirm */}
      <ConfirmDialog
        isOpen={!!approveUser}
        onClose={() => setApproveUser(null)}
        onConfirm={() => approveUser && approveMutation.mutate(approveUser.id)}
        title="Aprovo Perdoruesin"
        message={`Jeni te sigurt qe deshironi te aprovoni ${approveUser?.firstName} ${approveUser?.lastName}? Pas aprovimit, perdoruesi do te mund te logohet ne aplikacion.`}
        confirmText="Aprovo"
        variant="info"
        isLoading={approveMutation.isPending}
      />

      {/* Activate Confirm */}
      <ConfirmDialog
        isOpen={!!activateUser}
        onClose={() => setActivateUser(null)}
        onConfirm={() => activateUser && activateMutation.mutate(activateUser.id)}
        title="Aktivizo Perdoruesin"
        message={`Jeni te sigurt qe deshironi te aktivizoni ${activateUser?.firstName} ${activateUser?.lastName}?`}
        confirmText="Aktivizo"
        variant="info"
        isLoading={activateMutation.isPending}
      />

      {/* Send Manual Confirm */}
      <ConfirmDialog
        isOpen={!!sendManualUser}
        onClose={() => setSendManualUser(null)}
        onConfirm={() => sendManualUser && sendManualMutation.mutate(sendManualUser.id)}
        title="Dergo Manualin"
        message={`Jeni te sigurt qe deshironi te dergoni manualin e perdorimit tek ${sendManualUser?.firstName} ${sendManualUser?.lastName} (${sendManualUser?.email})?`}
        confirmText="Dergo"
        variant="info"
        isLoading={sendManualMutation.isPending}
      />

      {/* Delete User Confirm */}
      <ConfirmDialog
        isOpen={!!deleteUserTarget}
        onClose={() => setDeleteUserTarget(null)}
        onConfirm={() => deleteUserTarget && deleteUserMutation.mutate(deleteUserTarget.id)}
        title="Fshi Perdoruesin"
        message={`Jeni te sigurt qe deshironi te fshini ${deleteUserTarget?.firstName} ${deleteUserTarget?.lastName}? Kjo veprim nuk mund te kthehet mbrapsht.`}
        confirmText="Fshi"
        variant="danger"
        isLoading={deleteUserMutation.isPending}
      />

      {/* Reset Password Modal */}
      <Modal
        isOpen={!!resetPasswordUser}
        onClose={() => {
          setResetPasswordUser(null);
          setNewPassword('');
          setResetPasswordError('');
        }}
        title={`Rivendos Fjalekalimin: ${resetPasswordUser?.firstName} ${resetPasswordUser?.lastName}`}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Vendosni fjalekalimin e ri per kete perdorues.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fjalekalimi i ri
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setResetPasswordError('');
              }}
              className="input-field"
              placeholder="Te pakten 8 karaktere"
            />
            {resetPasswordError && (
              <p className="text-xs text-red-500 mt-1">{resetPasswordError}</p>
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmailOnReset}
              onChange={(e) => setSendEmailOnReset(e.target.checked)}
              className="rounded border-gray-300 text-barrikadave-500 focus:ring-barrikadave-500"
            />
            <span className="text-sm text-gray-600">Dergo fjalekalimin e ri me email</span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setResetPasswordUser(null);
                setNewPassword('');
                setResetPasswordError('');
              }}
              className="btn-ghost"
            >
              Anulo
            </button>
            <button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending}
              className={clsx(
                'bg-amber-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50',
              )}
            >
              {resetPasswordMutation.isPending ? 'Duke rivendosur...' : 'Rivendos'}
            </button>
          </div>
        </div>
      </Modal>

      <CsvImportWizard isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} />
    </div>
  );
}
